/**
 * ReviewChangeCard — Reader Polish Prompt 8 follow-up.
 *
 * Per-item card in the Review Changes dialog. Replaces the previous
 * truncated side-by-side paragraph view with:
 *   - Explicit Keep v1 / Apply v2 radio pair.
 *   - Inline word-level diff using diffWords (not a side-by-side
 *     truncated split).
 *   - "Why" line — pulls from rerun trace metadata when supplied,
 *     otherwise falls back to a generic "Generated as part of full
 *     rerun." line so the user always sees a reason.
 *   - 12-line cap with "Show all" expander.
 *   - Severity badge ('attention' / 'normal') driving header
 *     color + the flagged-needs-attention block.
 *
 * Pure presentation — the parent (DiffReviewDialog) owns the
 * decision state and passes the current selection in.
 */
import React, { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { diffWords, type DiffSegment } from '../../../lib/wordDiff';
import type { DiffSeverity } from '../../../lib/diffSeverity';

export type ReviewDecision = 'keep' | 'apply';

export interface ReviewChangeCardProps {
  section: string;
  label: string;
  oldValue: string;
  newValue: string;
  /** 'attention' renders the warning badge + sets the regression
   *  default below to 'keep'; 'normal' defaults to 'apply'. */
  severity: DiffSeverity;
  /** Optional badge text (eg "regression −20pt"). Rendered next to
   *  the label when severity === 'attention'. */
  badge?: string;
  /** Sections that share this content after dedup. Rendered as a
   *  footnote when length > 1. */
  appliesTo?: string[];
  /** Why the change happened — from rerun trace metadata. Falls
   *  back to "Generated as part of full rerun." when omitted. */
  why?: string;
  /** Score-style items use the diff helper but render the value
   *  pair inline (no word diff for "82/100" → "0/100"). */
  isScore?: boolean;
  decision: ReviewDecision;
  onDecide: (next: ReviewDecision) => void;
}

const VISIBLE_LINE_CAP = 12;

export function ReviewChangeCard(props: ReviewChangeCardProps) {
  const {
    section, label, oldValue, newValue, severity, badge,
    appliesTo, why, isScore, decision, onDecide,
  } = props;

  const segments = useMemo<DiffSegment[]>(() => {
    if (isScore) return []; // score items render the inline value pair.
    return diffWords(oldValue, newValue);
  }, [oldValue, newValue, isScore]);

  const [expanded, setExpanded] = useState(false);

  const isAttention = severity === 'attention';
  const wrapperTone = isAttention
    ? 'border-warning/50 bg-warning/5'
    : decision === 'apply'
      ? 'border-success/30 bg-success/5'
      : 'border-border bg-muted';

  const whyLine = why && why.trim().length > 0
    ? why
    : 'Generated as part of full rerun.';

  // Approximate visible lines for the cap by counting newlines in
  // the new value plus a soft wrap heuristic of ~80 chars per line.
  const longText = !isScore && (newValue.length > 800 || (newValue.match(/\n/g)?.length ?? 0) > VISIBLE_LINE_CAP);

  return (
    <div
      className={`border ${wrapperTone} p-3 space-y-2`}
      data-testid={`review-change-card-${section}`}
      data-severity={severity}
      data-decision={decision}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          {isAttention && (
            <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" aria-hidden="true" />
          )}
          <div className="min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="type-section text-text-primary">{label}</span>
              {isAttention && badge && (
                <span className="type-meta font-bold uppercase tracking-widest text-warning rounded-md border border-warning/40 bg-warning/10 px-1.5 py-0.5">
                  {badge}
                </span>
              )}
            </div>
            <div className="type-body text-text-muted">{whyLine}</div>
            {appliesTo && appliesTo.length > 1 && (
              <div className="type-meta text-text-muted mt-0.5" data-testid={`review-change-applies-to-${section}`}>
                applies to: {appliesTo.join(', ')}
              </div>
            )}
          </div>
        </div>

        <fieldset
          className="flex items-center gap-2 type-meta"
          aria-label={`Decision for ${label}`}
        >
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              name={`decision-${section}`}
              checked={decision === 'keep'}
              onChange={() => onDecide('keep')}
              className="accent-primary"
              data-testid={`review-change-keep-${section}`}
            />
            <span className={decision === 'keep' ? 'text-text-primary font-bold' : 'text-text-muted'}>Keep v1</span>
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              name={`decision-${section}`}
              checked={decision === 'apply'}
              onChange={() => onDecide('apply')}
              className="accent-primary"
              data-testid={`review-change-apply-${section}`}
            />
            <span className={decision === 'apply' ? 'text-text-primary font-bold' : 'text-text-muted'}>Apply v2</span>
          </label>
        </fieldset>
      </div>

      {/* Body — score-style or word diff. */}
      {isScore ? (
        <div className="flex items-center gap-2 text-[10px] font-mono">
          <span className="text-text-muted">v1:</span>
          <span className="text-text-secondary line-through">{oldValue || '—'}</span>
          <span className="text-text-muted">→</span>
          <span className="text-text-muted">v2:</span>
          <span className="text-text-primary font-bold">{newValue || '—'}</span>
        </div>
      ) : (
        <div
          className={`text-[10px] leading-relaxed text-text-primary whitespace-pre-wrap ${
            longText && !expanded ? 'max-h-[15em] overflow-hidden relative' : ''
          }`}
          data-testid={`review-change-body-${section}`}
        >
          {segments.map((seg, i) => {
            if (seg.kind === 'unchanged') return <span key={i}>{seg.text}</span>;
            if (seg.kind === 'added')     return <span key={i} className="bg-success/15 text-success-foreground rounded-sm">+{seg.text}</span>;
            return <span key={i} className="bg-danger/15 text-danger line-through rounded-sm">−{seg.text}</span>;
          })}
          {longText && !expanded && (
            <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-card to-transparent pointer-events-none" />
          )}
        </div>
      )}

      {longText && (
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="type-meta text-primary hover:text-primary/80 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
          data-testid={`review-change-expand-${section}`}
        >
          {expanded ? 'Show less' : 'Show all'}
        </button>
      )}
    </div>
  );
}
